/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { IonIcon, IonSpinner, useIonRouter } from "@ionic/react";
import {
  chevronBack,
  chevronDown,
  chevronForward,
  logOut,
} from "ionicons/icons";
import "./Header.css";
import useAuth from "../hooks/useAuth";
import { useBLE } from "../hooks/useBle";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import connectedBluetooth from "../assets/icons/connectedBluetooth.svg";
import disConnectedBluetooth from "../assets/icons/disConnectedBluetooth.svg";
import WifiOffIcon from "../assets/icons/wifi-off.svg";
import HeaderNotify, { HeaderNotificationItem } from "./ui/HeaderNotify";
import { FollowingList } from "../pages/settings/datasharing/DataSharingTypes";
import { getUserName } from "../utils/helpers";
import Avatar from "./ui/Avatar";
import { CURRENT_ENV } from "../services/api";
// One line at 24-26px, so tighter than getUserName's default (15 on phones).
const GREETING_NAME_MAX_LENGTH = 14;

interface HeaderProps {
  notificationsCount?: number;
  userName?: string;
  onNotificationClick?: () => void;
  showGreeting?: boolean;
  allPendingRequests?: any;
  showUserName?: boolean;
  showPageTitle?: boolean;
  pageTitle?: string;
  showNotification?: boolean;
  showUserIcon?: boolean;
  showPendingRequestsIcon?: boolean;
  showLogout?: boolean;
  avatarUrl?: string;
  closeAlert?: (closed: boolean) => void;
  timeOutDailyReminder?: boolean;
  showReminders?: HeaderNotificationItem[];
  showAvatar?: boolean;
  onUserIconClick?: () => void;
  weather?: string;
  showDropDown?: boolean;
  onDropDownChange?: (selected: any) => void;
  dropDownOptions?: { label: string; value: number | string }[];
  selectedDropDown?: { label: string; value: number | string };
  showBackButton?: boolean;
  onBackButtonClick?: () => void;
  showBtConnectionStatus?: boolean;
  isShortHeight?: boolean;
  handleFindDevice?: () => void;
  isAuthenticated?: boolean;
  userAvatars?: string[];
  following?: any;
  pageType?: "home" | "followers" | "following" | "settings";
  onOpenFollowingModal?: (followingData: FollowingList[]) => void;
  onOpenPendingRequestsModal?: (
    pendingRequests: FollowingList[]
  ) => void | undefined;
  subTitle?: string;
  loading?: boolean;
  greetingText?: string;
  showOfflineStatus?: boolean;
  hasActionsRequired?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  userName = "",
  allPendingRequests,
  showGreeting = true,
  greetingText: propGreetingText = "Welcome,",
  showUserName = false,
  showPageTitle = true,
  pageTitle = "",
  showUserIcon = true,
  showPendingRequestsIcon = false,
  showLogout = false,
  onOpenFollowingModal,
  closeAlert,
  showReminders,
  timeOutDailyReminder,
  pageType,
  following,
  selectedDropDown,
  showBackButton = false,
  onBackButtonClick,
  showBtConnectionStatus = false,
  isShortHeight = false,
  handleFindDevice,
  subTitle,
  loading,
  showOfflineStatus = false,
  hasActionsRequired = false,
}) => {
  const router = useIonRouter();
  const { logout } = useAuth();
  const handleLogout = () => {
    router.push("/signin");
    logout();
  };

  const handleBackButtonClick = () => {
    if (onBackButtonClick) {
      onBackButtonClick();
    } else {
      router.goBack();
    }
  };

  const [greetingText, setGreetingText] = React.useState(pageType === "home" ? "Welcome," : propGreetingText);

  // Show Welcome for 7 seconds on initial load
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setGreetingText((pageType === "home" ? "Dashboard," : greetingText));
    }, 7000);
    
    return () => clearTimeout(timer);
  }, []);
  // Utility functions for avatar handling

  const [followingData, setFollowingData] = useState<any[]>([]);
  const [avatars, setAvatars] = useState<{ label: string; value: number }[]>(
    []
  );
  useEffect(() => {
    if (following && Array.isArray(following)) {
      setFollowingData(following);
      // ⛔ `f.profile` was bare while `first_name` in the same expression was optional-chained.
      // This is a synchronous effect body with no try/catch, so one cached row without a
      // profile threw into App.tsx's `null`-rendering ErrorBoundary and blanked the app
      // (TD-165). `following` comes from Ionic Storage as well as the API, so its rows can be
      // any shape a previous app version persisted.
      const avatarOptions = following.map((f: any) => ({
        label: f?.profile?.avatar || f?.profile?.first_name?.charAt(0) || "",
        value: f?.profile?.id,
      }));
      setAvatars(avatarOptions);
    }
  }, [following]);
  // Adjust page title margin based on text length

  const getPageTitleMargin = (textLength: number): string => {
    // Base margin when back button is present
    const baseMargin = -45;

    // Adjust margin based on text length
    // Shorter text needs more negative margin to center properly
    if (textLength <= 5) {
      return `${baseMargin - 20}px`;
    } else if (textLength <= 10) {
      return `${baseMargin - 10}px`;
    } else if (textLength <= 15) {
      return `${baseMargin - 5}px`;
    } else if (textLength <= 20) {
      return `${baseMargin}px`;
    } else {
      // For longer text, less negative margin is needed
      return `${baseMargin + 25}px`;
    }
  };

  const { user } = useAuth();
  const isOnline = useNetworkStatus();
  const {
    isConnected,
    serialNumber,
    deviceType,
    isConnectClicked,
    parseDeviceString,
  } = useBLE();
  return (
    <div
      className="app-header"
      style={{
        position: "relative",
        zIndex: 10,
        marginBottom:
          pageType === "home" &&
          (!showReminders || showReminders.length === 0) &&
          !hasActionsRequired
            ? "8px"
            : undefined,
      }}
    >
      <div className="header-content flex items-center justify-between">
        <div className="user-greeting-container">
          {showBackButton && (
            <div className="back-button" onClick={handleBackButtonClick}>
              <IonIcon
                icon={chevronBack}
                style={{ fontSize: "24px", color: "#fff" }}
              />
            </div>
          )}
          {showGreeting && (
            <div className="greeting">
              {selectedDropDown?.label && selectedDropDown.label !== "Me"
                ? `Dashboard,`
                : greetingText}
              {!isOnline && showOfflineStatus && (
                <img
                  src={WifiOffIcon}
                  alt="Wifi-Off"
                  style={{
                    marginLeft: 6,
                    width: 25,
                    height: 25,
                    color: "white",
                  }}
                />
              )}
              {loading && isOnline && (
                <IonSpinner
                  name="bubbles"
                  style={{
                    marginLeft: 6,
                    width: 25,
                    height: 25,
                    color: "white",
                  }}
                />
              )}
            </div>
          )}
          {showUserName && (
            <div className={`username ${CURRENT_ENV}`}>
              {selectedDropDown?.label && selectedDropDown.label !== "Me"
                ? getUserName(selectedDropDown.label, GREETING_NAME_MAX_LENGTH)
                : getUserName(userName, GREETING_NAME_MAX_LENGTH)}
            </div>
          )}
        </div>
        {showPageTitle && pageTitle && (
            <div
            style={{
              marginLeft: showBackButton
              ? getPageTitleMargin(pageTitle.length)
              : "0px",
              flexDirection: "column",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
            className={"greeting"}
            >
            <div>{pageTitle}</div>
            {subTitle && <div className="page-subtitle">{subTitle}</div>}
            </div>
        )}

        <div className="header-icons">
          {/* {showNotification && (
            <div
              className="relative cursor-pointer"
              onClick={onNotificationClick}
            >
              <IonIcon
                icon={notificationsOutline}
                className="user-icon text-white"
                style={{ fontSize: "24px" }}
              />
               {notificationsCount && notificationsCount > 0 && (
                <span className="absolute -top-2 -right-2 flex items-center justify-center w-6 h-6 text-sm rounded-full bg-red-500 text-white">
                  {notificationsCount}
                </span>
              )} 
            </div>
          )} */}
          {/* {showUserIcon && (
            <div
              className="user-profile"
              onClick={() => {
                if (
                  notificationsCount &&
                  notificationsCount > 0 &&
                  onNotificationClick
                )
                  onNotificationClick();
                else if (onUserIconClick) {
                  onUserIconClick();
                } else {
                  router.push("/settings");
                }
              }}
            >
              {showAvatar && avatarUrl && !avatarUrl.match(/^[a-z-]+$/) ? (
                <>
                  {!imgError ? (
                    <img
                      src={avatarUrl}
                      onError={() => setImgError(true)}
                      alt={userName}
                      className="avatar"
                      crossOrigin="use-credentials"
                    />
                  ) : (
                    <Avatar
                      initials={
                        userName
                          ? userName
                              .split(" ")
                              .map((word) => word[0])
                              .slice(0, 2)
                              .join("")
                              .toUpperCase()
                          : "NA"
                      }
                      bgColor="transparent"
                    />
                  )}
                  {showNotification &&
                    notificationsCount &&
                    notificationsCount > 0 && (
                      <span
                        className={`${
                          notificationsCount > 0 ? "absolute" : "hidden"
                        } -top-2 -right-2 flex items-center justify-center w-6 h-6 text-sm rounded-full bg-red-500 text-white`}
                      >
                        {notificationsCount}
                      </span>
                    )}
                </>
              ) : (
                <div className="user-icon relative">
                  <img src={userIcon} alt="User Avatar" className="mb-0" />
                  {showNotification &&
                    notificationsCount &&
                    notificationsCount > 0 && (
                      <span
                        className={`${
                          notificationsCount > 0 ? "absolute" : "hidden"
                        } -top-2 -right-2 flex items-center justify-center w-6 h-6 text-sm rounded-full bg-red-500 text-white`}
                      >
                        {notificationsCount}
                      </span>
                    )}
                </div>
              )}
            </div>
          )} */}
          {showUserIcon && (
            <div className="mt-0 mb-1 flex items-center justify-end pr-0">
              <div
                className="stacked-avatars-container"
                onClick={() => onOpenFollowingModal?.(followingData)}
              >
                <div className="flex -space-x-2">
                  <Avatar
                    unstyled
                    imageSrc={user?.avatar}
                    initials={(
                      user?.first_name?.charAt(0) ||
                      user?.username?.charAt(0) ||
                      user?.email?.charAt(0) ||
                      user?.phone?.charAt(0) ||
                      "U"
                    ).toUpperCase()}
                    className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-[#007C9E] flex items-center justify-center text-white text-sm ring-1 ring-white ring-offset-0"
                    innerClassName=""
                    imageClassName="w-8 h-8 md:w-12 md:h-12 rounded-full object-cover"
                  />
                  {followingData.length > 0 ? (
                    followingData
                      .slice(0, 2)
                      .map((follower: any, index: number) => (
                        <Avatar
                          key={follower?.id || index}
                          unstyled
                          imageSrc={follower?.profile?.avatar}
                          initials={(
                            follower?.profile?.first_name?.charAt(0) ||
                            follower?.profile?.username?.charAt(0) ||
                            follower?.profile?.email?.charAt(0) ||
                            follower?.profile?.phone?.charAt(0) ||
                            "U"
                          ).toUpperCase()}
                          className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-[#007C9E] flex items-center justify-center text-white text-sm border-1 border-white"
                          innerClassName=""
                          imageClassName="w-8 h-8 md:w-12 md:h-12 rounded-full object-cover"
                        />
                      ))
                  ) : (
                    // <div
                    //   key={user?.profile_id}
                    //   className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white  text-sm border-2 border-white"
                    // >
                    //   {user?.photo_uuid ? (
                    //     <img
                    //       src={user?.photo_uuid}
                    //       className="w-8 h-8 rounded-full object-cover"
                    //     />
                    //   ) : (
                    //     (
                    //       user?.first_name?.charAt(0) ||
                    //       user?.username?.charAt(0) ||
                    //       user?.email?.charAt(0) ||
                    //       "U"
                    //     ).toUpperCase()
                    //   )}
                    // </div>
                    <></>
                  )}
                </div>
                {followingData.length > 2 && (
                  <div className="follower-count mx-1">
                    +
                    {followingData.length > 2
                      ? followingData.length - 2
                      : followingData.length}
                  </div>
                )}
                <div>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M11.06 5.52979L8 8.58312L4.94 5.52979L4 6.46979L8 10.4698L12 6.46979L11.06 5.52979Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
            </div>
          )}
          {showPendingRequestsIcon && (
            <div
              className="notification-container"
              onClick={() => {
                router.push(`/settings/pending-requests/:${pageType}`);
              }}
            >
              {allPendingRequests.length > 0 && (
                <div className="notification-container">
                  {allPendingRequests.length > 0 && (
                    <span className="notification-badge">
                      {allPendingRequests.length}
                    </span>
                  )}
                </div>
              )}
              <svg
                width="17"
                height="18"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 11.75H15.5V13.25H0.5V11.75H2V6.5C2 3.18629 4.68629 0.5 8 0.5C11.3137 0.5 14 3.18629 14 6.5V11.75ZM12.5 11.75V6.5C12.5 4.01472 10.4853 2 8 2C5.51472 2 3.5 4.01472 3.5 6.5V11.75H12.5ZM5.75 14.75H10.25V16.25H5.75V14.75Z"
                  fill="white"
                />
              </svg>
            </div>
          )}

          {showLogout && (
            <div className="user-icon" onClick={handleLogout}>
              <IonIcon icon={logOut} />
            </div>
          )}
        </div>
      </div>
      {pageType === "home" && showReminders && showReminders.length > 0 && (
        <div style={{ paddingTop: "10px" }}>
          <HeaderNotify
            existingNotifications={showReminders}
            closeAlert={(e) => {
              if (closeAlert) closeAlert(e);
            }}
            timeOutDailyReminder={timeOutDailyReminder ?? false}
          />
        </div>
      )}

      {/* {showDropDown && (
        <div
          className="flex justify-center mt-2"
          style={{ position: "relative", zIndex: 20 }}
        >
          <BiWazeDropdown
            onSelect={(selected: string) =>
              onDropDownChange && onDropDownChange(selected)
            }
            defaultValue={dropDownOptions[0]}
            options={dropDownOptions}
          />
        </div>
      )} */}
      {showBtConnectionStatus && (
        <div
          className={`mb-${
            isShortHeight ? "3" : "6"
          } flex justify-between items-center gap-4 pb-0`}
        >
          {/* Left side - Bluetooth icon and connection status */}
          <div className="flex items-center gap-2">
            <IonIcon
              src={isConnected ? connectedBluetooth : disConnectedBluetooth}
              style={{ fontSize: "18px" }}
            />
            <span
              className="text-sm font-normal tracking-[0.374px]"
              style={{
                color: isConnected ? "#7BD2EC" : "#FF9F0A",
                fontFamily: "Figtree",
              }}
            >
              {isConnected
                ? "Connected"
                : isConnectClicked.status
                ? "Connecting..."
                : "Disconnected"}
            </span>
          </div>

          {/* Right side - Device info or Find device */}
          <div className="flex-shrink-0">
            {isConnected && serialNumber.length > 0 ? (
              <div
                style={{
                  backgroundColor: "#1C1C1E",
                  // borderRadius: "20px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease-in-out",
                  minWidth: "fit-content",
                  maxWidth: "200px",
                }}
                onClick={handleFindDevice}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#2C2C2E";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#1C1C1E";
                }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="text-sm leading-tight break-words"
                    style={{
                      fontFamily: "Figtree",
                      fontStyle: "normal",
                      fontWeight: 400,
                      letterSpacing: "0.374px",
                      color: "#FFFFFF",
                    }}
                  >
                    {`${deviceType} S/N:${serialNumber}`}
                  </span>
                  <IonIcon
                    icon={chevronDown}
                    style={{
                      fontSize: "16px",
                      color: "#FFFFFF",
                      flexShrink: 0,
                    }}
                  />
                </div>
              </div>
            ) : isConnectClicked.status &&
              isConnectClicked.device?.device.name ? (
              <div
                style={{
                  backgroundColor: "#1C1C1E",
                  // borderRadius: "20px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease-in-out",
                  minWidth: "fit-content",
                  maxWidth: "200px",
                }}
                onClick={handleFindDevice}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#2C2C2E";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#1C1C1E";
                }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="text-sm leading-tight break-words"
                    style={{
                      fontFamily: "Figtree",
                      fontWeight: 400,
                      letterSpacing: "0.374px",
                      color: "#FFFFFF",
                    }}
                  >
                    {`${
                      parseDeviceString(isConnectClicked.device?.device.name)
                        .deviceType
                    } S/N:${
                      parseDeviceString(isConnectClicked.device?.device.name)
                        .serialNumber
                    }`}
                  </span>
                  <IonIcon
                    icon={chevronDown}
                    style={{
                      fontSize: "16px",
                      color: "#FFFFFF",
                      flexShrink: 0,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "#1C1C1E",
                  // borderRadius: "20px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease-in-out",
                  minWidth: "fit-content",
                  maxWidth: "200px",
                }}
                onClick={handleFindDevice}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#2C2C2E";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#1C1C1E";
                }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="text-sm"
                    style={{
                      color: "#7BD2EC",
                      fontFamily: "Figtree",
                      letterSpacing: "0.374px",
                      fontWeight: 400,
                    }}
                  >
                    Find device
                  </span>
                  <IonIcon
                    icon={chevronForward}
                    style={{
                      fontSize: "16px",
                      color: "#FFFFFF",
                      flexShrink: 0,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
